import { useEffect } from 'react';

import {
  useTableState,
  useTableDispatch,
  useTableBoundary,
} from '../context/TableSchemaBuilderContext';
import { parseCellId } from '../utils/cellHelpers';
import { useTableClipboard } from './useTableClipboard';
import { useCellRefs } from './useCellRefs';
import { getSelectionRange } from '../utils/selectionHelpers';

// Check if active element is an input field within the table boundary
const isInputInsideBoundary = (boundaryRef) => {
  const activeEl =
    typeof document !== 'undefined' ? document.activeElement : null;
  return (
    activeEl &&
    boundaryRef.current?.contains(activeEl) &&
    (activeEl.tagName === 'INPUT' ||
      activeEl.tagName === 'TEXTAREA' ||
      // @ts-ignore - contentEditable exists at runtime
      activeEl?.isContentEditable === true)
  );
};

export const useTableKeyboard = () => {
  const { schema, ui } = useTableState();
  const dispatch = useTableDispatch();
  const { handleCopy, handlePaste } = useTableClipboard();
  const { cellRefs } = useCellRefs();
  const boundaryRef = useTableBoundary();

  const handleClipboardShortcuts = (event, isEnable = false) => {
    if (!isEnable) {
      return false;
    }
    if (!event.ctrlKey) {
      return false;
    }
    if (event.key === 'c') {
      event.preventDefault();
      handleCopy();
      return true;
    }
    if (event.key === 'v') {
      event.preventDefault();
      handlePaste();
      return true;
    }
    return false;
  };

  const handleNavigation = (event, activeCell) => {
    const [col, row] = parseCellId(activeCell);
    const moves = {
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
    };

    if (!moves[event.key]) {
      return null;
    }

    event.preventDefault();
    const [deltaX, deltaY] = moves[event.key];
    const newCol = Math.min(Math.max(col + deltaX, 1), schema?.columns);
    const newRow = Math.min(Math.max(row + deltaY, 1), schema?.rows);

    return `${String.fromCharCode(64 + newCol)}${newRow}`;
  };

  const handleEditMode = (event, activeCell) => {
    if (
      ui.editMode ||
      event.ctrlKey ||
      event.metaKey ||
      event.key.length !== 1
    ) {
      return false;
    }

    dispatch({ type: 'SET_EDIT_MODE', payload: true });
    const cell = cellRefs.current?.[activeCell];
    if (cell) {
      cell.focus();
      cell.value = '';
    }
    return true;
  };

  const updateSelection = (
    newCellId,
    event,
    activeCell,
    isShiftSelectionEnable = false
  ) => {
    if (event.shiftKey) {
      if (!isShiftSelectionEnable) {
        return;
      }
      if (!ui.anchorCell) {
        dispatch({ type: 'SET_ANCHOR_CELL', payload: activeCell });
      }
      dispatch({
        type: 'UPDATE_SELECTION',
        payload: {
          currentCell: newCellId,
          cells: getSelectionRange(ui.anchorCell || activeCell, newCellId),
        },
      });
    } else {
      dispatch({
        type: 'UPDATE_SELECTION',
        payload: {
          currentCell: newCellId,
          cells: [newCellId],
          anchorCell: newCellId,
        },
      });
    }
  };

  // Auto-focus boundary when exiting edit mode to enable keyboard navigation
  useEffect(() => {
    if (
      !ui.editMode &&
      !isInputInsideBoundary(boundaryRef) &&
      boundaryRef.current?.focus
    ) {
      boundaryRef.current.focus?.();
    }
  }, [ui.editMode, boundaryRef]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if event originated inside our boundary
      // This prevents the event from being triggered when the user is typing in a cell
      // or when the user is focused on a different element outside the table
      if (!boundaryRef.current?.contains(event.target)) {
        return;
      }

      if (handleClipboardShortcuts(event, false)) {
        return;
      }

      if (!ui.selectedCells?.length) {
        return;
      }

      const activeCell = ui.currentCell || ui.selectedCells[0];

      if (handleEditMode(event, activeCell)) {
        return;
      }

      const newCellId = handleNavigation(event, activeCell);
      if (newCellId) {
        const currentActiveEl = cellRefs.current?.[activeCell];
        // Blur current cell to prevent input conflicts during navigation
        currentActiveEl?.blur?.();

        updateSelection(newCellId, event, activeCell);
        dispatch({ type: 'SET_EDIT_MODE', payload: false });

        // Refocus boundary to maintain keyboard event handling
        if (boundaryRef.current?.focus) {
          boundaryRef.current.focus?.();
        }
      }
    };

    // Attach listener to boundary element instead of window
    boundaryRef.current?.addEventListener('keydown', handleKeyDown);
    return () =>
      boundaryRef.current?.removeEventListener('keydown', handleKeyDown);
  }, [
    schema?.rows,
    schema?.columns,
    ui,
    dispatch,
    handleCopy,
    handlePaste,
    cellRefs,
    boundaryRef, // Added to dependency array to prevent stale closures
  ]);
};
